import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["editor", "hidden", "menu", "send"]
  static values = { people: Array }

  connect() {
    this.activeIndex = 0
    this.matches = []
    this.submitting = false
    this.syncHidden()
  }

  input() {
    this.normalizeEmptyEditor()
    this.syncHidden()
    this.renderMenu()
  }

  keydown(event) {
    if (this.menuTarget.hidden) return

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.activeIndex = Math.min(this.activeIndex + 1, this.matches.length - 1)
      this.renderItems()
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.activeIndex = Math.max(this.activeIndex - 1, 0)
      this.renderItems()
    } else if (event.key === "Enter" || event.key === "Tab") {
      if (this.matches[this.activeIndex]) {
        event.preventDefault()
        this.choosePerson(this.matches[this.activeIndex])
      }
    } else if (event.key === "Escape") {
      this.closeMenu()
    }
  }

  paste(event) {
    event.preventDefault()
    this.insertTextAtSelection(event.clipboardData.getData("text/plain"))
    this.input()
  }

  submit(event) {
    if (this.submitting) {
      event.preventDefault()
      return
    }

    this.syncHidden()

    if (this.hiddenTarget.value.trim() === "") {
      event.preventDefault()
      this.editorTarget.focus()
      return
    }

    this.submitting = true
    this.element.setAttribute("aria-busy", "true")
    if (this.hasSendTarget) this.sendTarget.disabled = true
  }

  blur() {
    window.setTimeout(() => this.closeMenu(), 150)
  }

  select(event) {
    event.preventDefault()
    const person = this.peopleValue.find((candidate) => candidate.id === event.currentTarget.dataset.personId)
    if (person) this.choosePerson(person)
  }

  renderMenu() {
    const token = this.currentToken()
    if (!token) {
      this.closeMenu()
      return
    }

    const query = token.query.toLowerCase()
    this.matches = this.peopleValue
      .filter((person) => {
        const haystack = person.name.toLowerCase()
        return haystack.includes(query)
      })
      .slice(0, 8)

    if (this.matches.length === 0) {
      this.closeMenu()
      return
    }

    this.activeIndex = 0
    this.menuTarget.hidden = false
    this.renderItems()
  }

  renderItems() {
    this.menuTarget.innerHTML = this.matches.map((person, index) => {
      const activeClass = index === this.activeIndex ? " discussion-composer__mention-option--active" : ""
      return `
        <button type="button"
                class="discussion-composer__mention-option${activeClass}"
                data-action="mousedown->discussion-composer#select"
                data-person-id="${this.escapeHtml(person.id)}">
          <span>${this.escapeHtml(person.name)}</span>
        </button>
      `
    }).join("")
  }

  choosePerson(person) {
    const token = this.currentToken()
    if (!token) return

    const range = document.createRange()
    range.setStart(token.node, token.startOffset)
    range.setEnd(token.node, token.endOffset)
    range.deleteContents()

    const mention = this.buildMention(person)
    const spacer = document.createTextNode(" ")
    const fragment = document.createDocumentFragment()
    fragment.appendChild(mention)
    fragment.appendChild(spacer)

    range.insertNode(fragment)

    this.editorTarget.focus()
    this.placeCaret(spacer, spacer.textContent.length)
    this.syncHidden()
    this.closeMenu()
  }

  currentToken() {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0 || !selection.isCollapsed) return null

    const range = selection.getRangeAt(0)
    if (!this.editorTarget.contains(range.startContainer)) return null
    if (range.startContainer.nodeType !== Node.TEXT_NODE) return null

    const prefix = range.startContainer.textContent.slice(0, range.startOffset)
    const match = prefix.match(/(^|\s)@([^\s@]{0,40})$/)
    if (!match) return null

    return {
      node: range.startContainer,
      startOffset: range.startOffset - match[2].length - 1,
      endOffset: range.startOffset,
      query: match[2]
    }
  }

  closeMenu() {
    this.menuTarget.hidden = true
    this.matches = []
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text || ""
    return div.innerHTML
  }

  buildMention(person) {
    const mention = document.createElement("span")
    mention.className = "discussion-composer__mention"
    mention.dataset.personId = person.id
    mention.dataset.personName = person.name
    mention.contentEditable = "false"
    mention.textContent = `@${person.name}`
    return mention
  }

  insertTextAtSelection(text) {
    const range = this.currentRange()
    if (!range) return

    range.deleteContents()
    const textNode = document.createTextNode(text.replace(/\r\n?/g, "\n"))
    range.insertNode(textNode)
    this.placeCaret(textNode, textNode.textContent.length)
  }

  currentRange() {
    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0)
      if (this.editorTarget.contains(range.commonAncestorContainer)) return range
    }

    const range = document.createRange()
    range.selectNodeContents(this.editorTarget)
    range.collapse(false)
    return range
  }

  placeCaret(node, offset) {
    const range = document.createRange()
    const selection = window.getSelection()
    range.setStart(node, offset)
    range.collapse(true)
    selection.removeAllRanges()
    selection.addRange(range)
  }

  syncHidden() {
    this.hiddenTarget.value = this.serializeNodes(this.editorTarget.childNodes)
  }

  serializeNodes(nodes) {
    return Array.from(nodes).map((node) => this.serializeNode(node)).join("")
  }

  serializeNode(node) {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent || ""
    if (node.nodeType !== Node.ELEMENT_NODE) return ""

    if (node.dataset.personId) {
      return `@[${node.dataset.personName}](person:${node.dataset.personId})`
    }

    if (node.tagName === "BR") return "\n"

    const serialized = this.serializeNodes(node.childNodes)
    if (node.tagName === "DIV" || node.tagName === "P") return `${serialized}\n`

    return serialized
  }

  normalizeEmptyEditor() {
    if (this.editorTarget.textContent === "" && this.editorTarget.querySelectorAll(".discussion-composer__mention").length === 0) {
      this.editorTarget.innerHTML = ""
    }
  }
}
